/**
 * QRCode — Realistic-looking SVG QR code (static, decorative)
 * Uses finder patterns, alignment pattern, and pseudo-random data modules.
 */
export default function QRCode({ size = 180, color = '#1A1A2E', bg = '#ffffff' }) {
  // QR code is a 21×21 grid (Version 1 QR pattern approximation)
  const modules = 21;
  const cellSize = size / modules;

  // Finder pattern at top-left, top-right, bottom-left
  // Timing patterns, and pseudo-random data modules
  const finderPattern = (ox, oy) => (
    <>
      {/* Outer 7×7 dark border */}
      <rect x={ox * cellSize} y={oy * cellSize} width={7 * cellSize} height={7 * cellSize} fill={color} rx={1}/>
      {/* Inner 5×5 white */}
      <rect x={(ox + 1) * cellSize} y={(oy + 1) * cellSize} width={5 * cellSize} height={5 * cellSize} fill={bg}/>
      {/* Inner 3×3 dark */}
      <rect x={(ox + 2) * cellSize} y={(oy + 2) * cellSize} width={3 * cellSize} height={3 * cellSize} fill={color}/>
    </>
  );

  // Simple deterministic "data" pattern
  const seed = [
    [0,1,1,0,1,0,1,0,0,0,1,0,1,0],
    [1,0,1,1,0,0,0,1,1,0,0,1,0,1],
    [0,1,0,1,1,0,1,0,0,1,1,0,1,0],
    [1,1,0,0,0,1,0,1,0,1,0,1,0,0],
    [0,0,1,1,0,0,1,1,0,0,1,0,1,1],
    [1,0,0,1,1,0,0,1,1,1,0,1,0,0],
    [0,1,1,0,0,1,1,0,0,1,0,0,1,1],
    [1,0,1,0,1,0,1,0,1,0,1,1,0,1],
  ];

  const dataModules = [];
  for (let r = 9; r < modules; r++) {
    for (let c = 0; c < modules; c++) {
      // skip bottom-left finder area
      if (r >= 14 && c < 8) continue;
      const val = seed[r % seed.length][c % seed[0].length];
      if (val) {
        dataModules.push(
          <rect
            key={`d-${r}-${c}`}
            x={c * cellSize}
            y={r * cellSize}
            width={cellSize}
            height={cellSize}
            fill={color}
          />
        );
      }
    }
  }
  // top-right data area
  for (let r = 0; r < 8; r++) {
    for (let c = 8; c < modules - 8; c++) {
      const val = seed[r % seed.length][c % seed[0].length];
      if (val) {
        dataModules.push(
          <rect
            key={`dr-${r}-${c}`}
            x={c * cellSize}
            y={r * cellSize}
            width={cellSize}
            height={cellSize}
            fill={color}
          />
        );
      }
    }
  }

  // Timing patterns (row 6, col 6)
  const timingH = [];
  for (let c = 8; c < modules - 8; c++) {
    if (c % 2 === 0) timingH.push(
      <rect key={`th-${c}`} x={c * cellSize} y={6 * cellSize} width={cellSize} height={cellSize} fill={color}/>
    );
  }
  const timingV = [];
  for (let r = 8; r < modules - 8; r++) {
    if (r % 2 === 0) timingV.push(
      <rect key={`tv-${r}`} x={6 * cellSize} y={r * cellSize} width={cellSize} height={cellSize} fill={color}/>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="crispEdges"
    >
      <rect width={size} height={size} fill={bg}/>
      {/* Data modules */}
      {dataModules}
      {timingH}
      {timingV}
      {/* Finder patterns */}
      {finderPattern(0, 0)}
      {finderPattern(modules - 7, 0)}
      {finderPattern(0, modules - 7)}
      {/* Format info dots */}
      <rect x={8 * cellSize} y={0} width={cellSize} height={cellSize} fill={color}/>
      <rect x={8 * cellSize} y={cellSize} width={cellSize} height={cellSize} fill={color}/>
      <rect x={8 * cellSize} y={7 * cellSize} width={cellSize} height={cellSize} fill={color}/>
    </svg>
  );
}
